import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { sessionManager } from '../utils/sessionManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing Supabase session
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      // Check if session should be cleared (user didn't check "Keep me logged in" and browser was closed)
      const isValid = await sessionManager.checkSessionValidity();
      
      if (!isValid) {
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await loadUserData(session.user.id);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = async (userId) => {
    try {
      // First, try to fetch from users table (for regular users)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (userData && !userError) {
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.full_name,
          phone: userData.phone,
          membershipLevel: userData.membership_tier,
          status: userData.status,
          isAdmin: false,
          profilePicture: userData.profile_picture || '/images/default-profile.jpg'
        });
        return;
      }

      // If not found in users table, check admins table
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (adminData && !adminError) {
        setUser({
          id: adminData.user_id,
          email: adminData.email,
          name: adminData.full_name,
          phone: '',
          membershipLevel: 'Admin',
          status: 'approved',
          isAdmin: true,
          profilePicture: adminData.profile_picture || '/images/default-profile.jpg'
        });
        return;
      }

      // If neither found, throw error
      throw new Error('User not found in database');
    } catch (error) {
      console.error('Error loading user data:', error);
      setUser(null);
    }
  };

  const login = (email, password) => {
    // This function is kept for backward compatibility
    // Actual login is handled in Login.js with Supabase
    const userData = {
      email,
      name: email.split('@')[0],
      membershipLevel: 'Silver',
      profilePicture: '/images/default-profile.jpg'
    };
    setUser(userData);
    return { success: true };
  };

  const logout = async () => {
    try {
      await sessionManager.logout();
      setUser(null);
      localStorage.removeItem('ibv_user');
      localStorage.removeItem('ibv-session-type');
      localStorage.removeItem('ibv-remember-me');
      localStorage.removeItem('ibv-no-remember');
      sessionStorage.clear();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) return;

      // Prepare update object
      const updateData = {};
      if (updates.name !== undefined) updateData.full_name = updates.name;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.profilePicture !== undefined) updateData.profile_picture = updates.profilePicture;
      
      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        updateData.updated_at = new Date().toISOString();
        
        const { error } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', authUser.id);

        if (error) throw error;
      }

      // Update local user state
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
